import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator
} from '@mui/lab';
import moment from 'moment';
import React, { useCallback, useEffect } from 'react';
import { LocationSection } from '../location';

export const TimelineComponent = ({ dataList, hideDuration = false, timelineDot = '', showTotalDuration = false }) => {
  const [totalDuration, setTotalDuration] = React.useState([]);

  const calculateTotalDuration = useCallback(() => {
    return dataList?.map((experienceItem) => {
      const totalMonths = experienceItem?.relevantBackground?.reduce((acc, item) => {
        const monthCount = getTotalMonths(item?.startingFrom, item?.endedAt);
        return acc + Number(monthCount);
      }, 0);

      return convertMonthsToYearsAndMonths(totalMonths);
    });
  }, [dataList]);

  useEffect(() => {
    if (showTotalDuration) {
      const total = calculateTotalDuration();
      setTotalDuration(total);
    }
  }, [dataList, calculateTotalDuration, showTotalDuration]);

  const getTotalMonths = (startDate, endDate) => {
    const end = endDate || moment().valueOf();
    const diff = moment(end).diff(moment(startDate), 'months');

    return diff === 0 ? 1 : diff + 1;
  };

  const convertMonthsToYearsAndMonths = (totalMonths) => {
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    return { years, months };
  };

  const formatYearsAndMonths = (years, months) => {
    const yearString = years > 0 ? `${years} Yr${years > 1 ? 's' : ''}` : '';

    const monthString = months > 0 ? `${months} mo${months > 1 ? 's' : ''}` : '';

    if (years && months) return `${yearString}, ${monthString}`;
    if (years) return yearString;
    if (months) return monthString;

    return '1 mo';
  };

  const calculateDuration = (startDate, endDate, onlyMonths = false) => {
    const totalMonths = getTotalMonths(startDate, endDate);
    const { years, months } = convertMonthsToYearsAndMonths(totalMonths);

    if (onlyMonths) {
      return totalMonths;
    }
    return formatYearsAndMonths(years, months);
  };

  return dataList?.map((experienceItem, key) => (
    <div className='flex flex-col' key={key}>
      <div className='flex items-start gap-2'>
        <img
          src={experienceItem?.organizationProfile?.imageUrl}
          alt='Organization logo'
          width='32px'
          height='32px'
          className='shadow-box'
        />
        <div className='flex flex-col'>
          <h2>{experienceItem?.organizationProfile?.title}</h2>
          <div className='flex md:items-center md:gap-1 flex-col md:flex-row'>
            {showTotalDuration && (
              <span className='small text-secondaryMineShaft flex gap-1'>
                {formatYearsAndMonths(totalDuration[key]?.years, totalDuration[key]?.months)}
                <span className='hidden md:block'>&middot;</span>
              </span>
            )}
            <LocationSection locationTitle={experienceItem?.organizationProfile?.location} />
          </div>
        </div>
      </div>
      <Timeline
        className='!pt-0 !pl-1.5 !md:pl-8'
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}>
        {experienceItem?.relevantBackground?.map((item, subKey) => (
          <TimelineItem key={subKey}>
            <TimelineSeparator className='mt-1.5'>
              {timelineDot || <TimelineDot className={!item?.endedAt && '!bg-green'} variant='outlined' />}

              {experienceItem?.relevantBackground?.length <= 1 && <TimelineConnector />}
              {experienceItem?.relevantBackground?.length !== subKey + 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent className='!pl-4 !pt-1 !pr-0'>
              <h3 className='text-xl/[1.35]'>{item?.title}</h3>
              <div className='grid md:gap-2 md:grid-cols-[1fr,_1.5fr]'>
                <div className='flex flex-col'>
                  <LocationSection locationTitle={item?.location} />
                </div>
                <div className='flex gap-1 md:flex-row md:items-end md:justify-end'>
                  <p className='small !tracking-[0.5px] w-fit text-nowrap'>
                    {`${renderTimeStamp(item?.startingFrom)} - ${renderTimeStamp(item?.endedAt)}`}
                  </p>
                  {!hideDuration && (
                    <p className='small !tracking-[0.5px] w-fit text-nowrap'>
                      ({calculateDuration(item?.startingFrom, item?.endedAt)})
                    </p>
                  )}
                </div>
              </div>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
      {dataList?.length !== key + 1 && <div className='bg-satinGrey w-full h-px mb-[6px]'></div>}
    </div>
  ));
};

const renderTimeStamp = (param) => {
  return param ? moment(param).format(`MMM'YY`) : 'Present';
};
